/*
 ============================================================
  CUSTOMER ACCOUNT API
  Every read below is additionally protected by RLS in Supabase
  (see backend/sql/customer_accounts.sql) — the filters here are
  for clarity, not security.
 ============================================================
*/
import { supabase } from '../backend/lib/supabaseClient';
import { birthNumberFromDate, planetFromNumber } from '../utils/astrology';

/* =========================
   AUTH
========================= */
export const signUp = async ({ email, password, fullName, phone }) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: { full_name: fullName || '', phone: phone || '' },
    },
  });
  return { data, error };
};

export const signIn = async ({ email, password }) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const changeMyPassword = async ({ email, currentPassword, newPassword }) => {
  // Re-authenticate first: an open session alone shouldn't be
  // enough to change the password.
  const { error: reauthError } = await supabase.auth.signInWithPassword({
    email,
    password: currentPassword,
  });

  if (reauthError) {
    return { error: { message: 'Current password is incorrect.' } };
  }

  const { error } = await supabase.auth.updateUser({ password: newPassword });
  return { error };
};

/* =========================
   PROFILE
========================= */
export const getProfile = async (userId) => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  return { data, error };
};

const isMissingFunction = (error) =>
  !!error &&
  (error.code === '42883' ||
    error.code === 'PGRST202' ||
    /could not find the function/i.test(error.message || '') ||
    /function .* does not exist/i.test(error.message || ''));

export const updateProfile = async (userId, email, updates) => {
  // Preferred path: a SECURITY DEFINER function that derives the row
  // id from auth.uid(). Writing users directly means satisfying both
  // the INSERT and UPDATE policies, which upsert makes fiddly.
  const { data, error } = await supabase.rpc('save_my_profile', {
    p_full_name: updates.full_name ?? null,
    p_phone: updates.phone ?? null,
    p_birth_date: updates.birth_date || null,
    p_house: updates.address_house ?? null,
    p_street: updates.address_street ?? null,
    p_landmark: updates.address_landmark ?? null,
    p_city: updates.address_city ?? null,
    p_state: updates.address_state ?? null,
    p_pincode: updates.address_pincode ?? null,
  });

  if (!error) return { data, error: null };

  // Fall back only when the function hasn't been created yet, so a
  // half-applied schema still lets someone save their details.
  if (!isMissingFunction(error)) return { data: null, error };

  const payload = { id: userId, email, ...updates };
  if ('birth_date' in payload) {
    const number = birthNumberFromDate(payload.birth_date);
    payload.birth_number = number;
    payload.planet = planetFromNumber(number);
  }

  const fallback = await supabase
    .from('users')
    .upsert(payload, { onConflict: 'id' })
    .select()
    .maybeSingle();

  return { data: fallback.data, error: fallback.error };
};

/* Save just the delivery address — used by checkout's "save this
   address" tick, so it doesn't disturb the rest of the profile. */
export const saveAddress = async (userId, email, address, extras = {}) =>
  updateProfile(userId, email, { ...address, ...extras });

/* Attach past WhatsApp/guest orders that used this phone number. */
export const claimOrdersByPhone = async (phone) => {
  const { data, error } = await supabase.rpc('claim_orders_by_phone', {
    p_phone: phone,
  });
  return { claimed: data || 0, error };
};

/* =========================
   ORDERS
========================= */
export const getMyOrders = async (userId) => {
  const { data, error } = await supabase
    .from('orders')
    .select('*, order_items(*)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  return { data: data || [], error };
};

/* =========================
   CONSULTATIONS
========================= */
export const getMyConsultations = async (userId) => {
  const { data, error } = await supabase
    .from('consultations')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  return { data: data || [], error };
};

export const requestConsultation = async (userId, details) => {
  const { data, error } = await supabase
    .from('consultations')
    .insert({
      user_id: userId,
      customer_name: details.customerName,
      customer_phone: details.customerPhone,
      customer_email: details.customerEmail || null,
      birth_date: details.birthDate || null,
      birth_time: details.birthTime || null,
      birth_place: details.birthPlace || null,
      notes: details.notes || null,
      status: 'pending',
    })
    .select()
    .single();

  return { data, error };
};
